import { useEffect, useMemo } from 'react';
import { useWatch } from 'react-hook-form';
import { FilterOption } from '@britishcouncil/react-admin';
import { OnlineSpeakingMonitoringResult } from 'api';
import { useExaminerId, useFormContext } from 'components';
import {
  useOnlineSpeakingMonitoringResultsForNewQuery,
  useOnlineSpeakingMonitoringResultsForExistingQuery,
} from 'store';
import { OnlineSpeakingMonitoringFormData } from './onlineSpeakingMonitoringValidation';
import { ONLINE_SPEAKING_MONITORING_RESULT_OPTIONS } from './onlineSpeakingMonitoringOptions';

type UseOnlineSpeakingMonitoringResultOptionsReturn = {
  resultOptions: FilterOption<OnlineSpeakingMonitoringResult>[];
  isLoadingResults: boolean;
};

export const useOnlineSpeakingMonitoringResultOptions = (
  monitoringId?: number
): UseOnlineSpeakingMonitoringResultOptionsReturn => {
  const examinerId = useExaminerId();
  const isExisting = monitoringId !== undefined;

  const {
    formMethods: { control, setValue },
  } = useFormContext<OnlineSpeakingMonitoringFormData>();
  const [monitoringDate, selectedResult] = useWatch({
    control,
    name: ['monitoringDate', 'result'],
  });

  const { data: newResultsData, isLoading: isLoadingNew } =
    useOnlineSpeakingMonitoringResultsForNewQuery(
      { examinerId, monitoringDate: monitoringDate },
      { skip: isExisting || !monitoringDate, refetchOnMountOrArgChange: true }
    );

  const { data: existingResultsData, isLoading: isLoadingExisting } =
    useOnlineSpeakingMonitoringResultsForExistingQuery(
      { id: monitoringId!, monitoringDate: monitoringDate },
      { skip: !isExisting || !monitoringDate, refetchOnMountOrArgChange: true }
    );

  const resultsData = isExisting ? existingResultsData : newResultsData;
  const isLoadingResults = isExisting ? isLoadingExisting : isLoadingNew;

  const resultOptions = useMemo(() => {
    if (resultsData) {
      return ONLINE_SPEAKING_MONITORING_RESULT_OPTIONS.filter(({ value }) =>
        resultsData.results.includes(value)
      );
    }
    return [];
  }, [resultsData]);

  useEffect(() => {
    if (resultsData && selectedResult && !resultsData.results.includes(selectedResult)) {
      const value =
        resultsData.results.length == 1
          ? resultsData.results[0]
          : (undefined as NonNullable<undefined>);

      setValue('result', value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resultsData, selectedResult]);

  return { resultOptions, isLoadingResults };
};
